import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Bot, Globe, Layers } from 'lucide-react';

interface AgentSelectorProps {
  currentAgent: string;
  onAgentChange: (agent: string) => void;
}

const AVAILABLE_AGENTS = [
  { id: 'search_engine', name: 'Search Engine', icon: Globe },
  { id: 'multi_domain', name: 'Multi-Domain', icon: Layers }
];

const AgentSelector: React.FC<AgentSelectorProps> = ({ currentAgent, onAgentChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (!buttonRef.current) return;
      
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const dropdownHeight = 200; // Approximate height of dropdown

      setDropdownPosition(spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top');
    };

    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition);
      window.addEventListener('resize', updateDropdownPosition);
    }

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen]);

  const currentAgentInfo = AVAILABLE_AGENTS.find(a => a.id === currentAgent) || AVAILABLE_AGENTS[0];
  const Icon = currentAgentInfo.icon;

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center sm:justify-between px-2 sm:px-3 py-1.5 text-sm rounded-md border border-[var(--rhea-card-border)] bg-[var(--rhea-bg-primary)] hover:bg-[var(--rhea-bg-tertiary)] transition-all duration-200"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[var(--rhea-text-secondary)]" />
          <span className="text-[var(--rhea-text-primary)] hidden sm:block">{currentAgentInfo.name}</span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-[var(--rhea-text-secondary)] transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div 
          className={`absolute ${
            dropdownPosition === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1'
          } left-0 min-w-48 sm:w-full bg-[var(--rhea-bg-primary)] border border-[var(--rhea-card-border)] rounded-md shadow-lg z-50`}
        >
          {AVAILABLE_AGENTS.map((agent) => (
            <button
              key={agent.id}
              onClick={() => {
                onAgentChange(agent.id);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--rhea-bg-tertiary)] transition-colors flex items-center gap-2 ${
                agent.id === currentAgent ? 'text-[var(--rhea-accent-primary)]' : 'text-[var(--rhea-text-primary)]'
              }`}
            >
              <agent.icon className="w-4 h-4 text-[var(--rhea-text-secondary)]" />
              {agent.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentSelector; 